import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/Index";
import { User } from "../../store/slice/AuthSlice";
import {
  Box,
  Fab,
  Tooltip,
  Paper,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  CircularProgress,
  Collapse,
  useTheme,
  Badge,
  Divider,
} from "@mui/material";
import {
  AutoAwesome,
  Close,
  Send,
  Minimize,
  DeleteOutline,
  PersonOutline,
  ElectricBoltOutlined,
  EventNoteOutlined,
  AnalyticsOutlined,
  East,
  SensorsOutlined,
  ChatBubbleOutline,
  StorageOutlined,
  SearchOutlined,
  PhoneInTalkOutlined,
  CheckCircleOutline,
  ErrorOutline,
  ExpandMore,
  ExpandLess,
  SmartToyOutlined,
  CleaningServicesOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import apiService, { BaseURL } from "../../service/Api";
import { VenueData } from "../../pages/venues/types/venues.types";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
}

export interface AgentActivityEvent {
  id: string;
  event_type:
  | "whatsapp"
  | "sql"
  | "search"
  | "image_search"
  | "web_scrape"
  | "task_start"
  | "task_complete"
  | "thought"
  | "error"
  | "info"
  | "init"
  | "ping";
  title: string;
  details?: string;
  status: "running" | "success" | "error" | "info";
  timestamp: string;
  formatted_time?: string;
  extra?: Record<string, any>;
}

export interface AgentStatusResponse {
  status: "idle" | "active" | "thinking";
  current_task?: string;
  active_tasks_count?: number;
  total_tasks_completed?: number;
}

interface FormattedMessageTextProps {
  content: string;
  isUser?: boolean;
}

const FormattedMessageText: React.FC<FormattedMessageTextProps> = ({
  content,
  isUser,
}) => {
  if (!content) return null;

  const lines = content.split("\n");

  const parseInlineMarkdown = (text: string) => {
    // Regex for inline markdown: **bold**, *italic*, `code`, [link](url)
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
    const parts = text.split(regex);

    return parts.map((part, idx) => {
      if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
        return (
          <Box
            key={idx}
            component="span"
            sx={{ fontWeight: 700, color: isUser ? "#ffffff" : "inherit" }}
          >
            {part.slice(2, -2)}
          </Box>
        );
      }
      if (
        (part.startsWith("*") && part.endsWith("*") && part.length > 2) ||
        (part.startsWith("_") && part.endsWith("_") && part.length > 2)
      ) {
        return (
          <Box key={idx} component="span" sx={{ fontStyle: "italic" }}>
            {part.slice(1, -1)}
          </Box>
        );
      }
      if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
        return (
          <Box
            key={idx}
            component="span"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.85em",
              bgcolor: isUser ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.06)",
              px: 0.6,
              py: 0.2,
              borderRadius: 1,
            }}
          >
            {part.slice(1, -1)}
          </Box>
        );
      }
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        return (
          <Box
            key={idx}
            component="a"
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: isUser ? "#ffffff" : "#724e41",
              textDecoration: "underline",
              fontWeight: 600,
            }}
          >
            {linkMatch[1]}
          </Box>
        );
      }
      return part;
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {lines.map((line, lineIdx) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return <Box key={lineIdx} sx={{ height: 4 }} />;
        }

        if (trimmed.startsWith("### ")) {
          return (
            <Typography
              key={lineIdx}
              variant="subtitle2"
              sx={{ fontWeight: 700, mt: 0.5, mb: 0.2 }}
            >
              {parseInlineMarkdown(trimmed.slice(4))}
            </Typography>
          );
        }
        if (trimmed.startsWith("## ") || trimmed.startsWith("# ")) {
          return (
            <Typography
              key={lineIdx}
              variant="subtitle1"
              sx={{ fontWeight: 700, mt: 0.5, mb: 0.2 }}
            >
              {parseInlineMarkdown(trimmed.replace(/^#+\s*/, ""))}
            </Typography>
          );
        }

        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <Box
              key={lineIdx}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                pl: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  bgcolor: isUser ? "#ffffff" : "currentColor",
                  mt: 0.9,
                  flexShrink: 0,
                  opacity: 0.8,
                }}
              />
              <Typography variant="body2" sx={{ lineHeight: 1.5, fontSize: 13 }}>
                {parseInlineMarkdown(trimmed.slice(2))}
              </Typography>
            </Box>
          );
        }

        return (
          <Typography
            key={lineIdx}
            variant="body2"
            sx={{ lineHeight: 1.5, fontSize: 13, wordBreak: "break-word" }}
          >
            {parseInlineMarkdown(line)}
          </Typography>
        );
      })}
    </Box>
  );
};

const BrandMIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 18,
  color = "currentColor",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.5 19V5.5L12 13.5L20.5 5.5V19"
      stroke={color}
      strokeWidth="3.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19 2.2L19.7 3.8L21.3 4.5L19.7 5.2L19 6.8L18.3 5.2L16.7 4.5L18.3 3.8L19 2.2Z"
      fill={color}
    />
  </svg>
);

interface SiteVisitAiFloatingChatProps {
  venueData?: VenueData;
}

export const SiteVisitAiFloatingChat: React.FC<SiteVisitAiFloatingChatProps> = ({
  venueData,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"chat" | "activity">("chat");

  // Chat State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Agent Activity Tracker State
  const [agentEvents, setAgentEvents] = useState<AgentActivityEvent[]>([]);
  const [agentStatus, setAgentStatus] = useState<AgentStatusResponse | null>(null);
  const [activityFilter, setActivityFilter] = useState<string>("all");
  const [activitySearchQuery, setActivitySearchQuery] = useState<string>("");
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

  const user = useSelector((state: RootState) => state.auth.user) as User | null;

  const userName = useMemo(() => {
    if (user?.first_name?.trim()) {
      return user.first_name.trim().split(" ")[0];
    }
    if (user?.username?.trim()) {
      const cleanUser = user.username.trim().split("@")[0].split(".")[0];
      return cleanUser.charAt(0).toUpperCase() + cleanUser.slice(1);
    }
    return "";
  }, [user]);

  // Scroll chat to bottom whenever messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen && activeTab === "chat") {
      scrollToBottom();
    }
  }, [messages, isOpen, loading, activeTab]);

  // Fetch activity history & status via REST API (reverses array so newest is at top)
  const fetchActivityAndStatus = async () => {
    try {
      const [actRes, statusRes] = await Promise.all([
        apiService.get<AgentActivityEvent[]>("/api/venues/agent/activity/"),
        apiService.get<AgentStatusResponse>("/api/venues/agent/status/"),
      ]);

      if (Array.isArray(actRes.data)) {
        // Reverse array so newest activity events appear at top of list
        const reversed = [...actRes.data].reverse();
        setAgentEvents(reversed);
      }
      if (statusRes.data) {
        setAgentStatus(statusRes.data);
      }
    } catch (err) {
      console.warn("Error fetching agent activity:", err);
    }
  };

  // Subscribe to Realtime Agent Activity Stream (SSE) & REST polling fallback
  useEffect(() => {
    if (!isOpen) return;

    // 1. Initial fetch
    fetchActivityAndStatus();

    // 2. Periodic polling every 2 seconds for guaranteed realtime updates
    const pollInterval = setInterval(() => {
      fetchActivityAndStatus();
    }, 2000);

    // 3. Establish live Server-Sent Events (SSE) connection
    let eventSource: EventSource | null = null;
    try {
      const streamPath = "/api/venues/agent/activity/stream/";
      const streamUrl = BaseURL.startsWith("http")
        ? `${BaseURL}${streamPath}`
        : streamPath;
      eventSource = new EventSource(streamUrl);

      eventSource.onmessage = (event) => {
        try {
          const data: AgentActivityEvent = JSON.parse(event.data);
          if (data.event_type === "init") {
            if (Array.isArray((data as any).history)) {
              setAgentEvents([...(data as any).history].reverse());
            }
            if ((data as any).agent_status) {
              setAgentStatus((data as any).agent_status);
            }
          } else if (data.event_type === "ping") {
            if ((data as any).agent_status) {
              setAgentStatus((data as any).agent_status);
            }
          } else {
            setAgentEvents((prev) => {
              if (prev.some((e) => e.id === data.id)) return prev;
              return [data, ...prev];
            });
          }
        } catch (e) {
          console.error("Failed to parse SSE agent event:", e);
        }
      };

      eventSource.onerror = (err) => {
        console.warn("Agent activity SSE connection notice:", err);
      };
    } catch (e) {
      console.warn("Could not initiate EventSource connection:", e);
    }

    return () => {
      clearInterval(pollInterval);
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [isOpen]);

  const handleSendMessage = async (textToSend?: string) => {
    const queryText = (textToSend || input).trim();
    if (!queryText || loading) return;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: queryText,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    if (!textToSend) setInput("");
    setLoading(true);

    // Optimistically update agent status & add running activity event
    setAgentStatus({
      status: "active",
      current_task: `Web Chat Query: "${queryText.slice(0, 45)}..."`,
    });

    const tempEvent: AgentActivityEvent = {
      id: `temp-${Date.now()}`,
      event_type: "task_start",
      title: "Processing Interactive Web Chat Query",
      details: `User Prompt: ${queryText}`,
      status: "running",
      timestamp: new Date().toISOString(),
      formatted_time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
    };
    setAgentEvents((prev) => [tempEvent, ...prev]);

    try {
      // Build system context payload if venue data is provided
      let fullPrompt = queryText;
      if (venueData && messages.length <= 2) {
        fullPrompt = `[Context: Viewing Venue ID ${venueData.id} - ${venueData.name
          }, City: ${venueData.city || "N/A"}, Power: ${venueData.has_power ? "Yes" : "No"
          }, ISP: ${venueData.internet_service_provider || "N/A"}]\n${queryText}`;
      }

      // Prepare payload for backend agent/chat endpoint
      const payloadMessages = updatedMessages.map((m) => ({
        role: m.role,
        content: m.id === userMsg.id ? fullPrompt : m.content,
      }));

      const res = await apiService.post<{
        response?: string;
        messages?: Array<{ role: string; content: string }>;
      }>("/api/venues/agent/chat/", {
        messages: payloadMessages,
      });

      const rawResponse =
        res.data?.response ||
        (res.data?.messages && res.data.messages.length > 0
          ? res.data.messages[res.data.messages.length - 1].content
          : "I received your request successfully.");

      const cleanResponseText = (text: string): string => {
        if (!text) return "";
        let clean = text;
        if (
          clean.startsWith("[{'type': 'text'") ||
          clean.startsWith('[{"type": "text"') ||
          clean.startsWith("[{")
        ) {
          const match =
            clean.match(/'text':\s*["']([\s\S]*?)["']\s*,\s*['"]extras['"]/i) ||
            clean.match(/"text":\s*["']([\s\S]*?)["']\s*,\s*["']extras["']/i) ||
            clean.match(/['"]text['"]:\s*['"]([\s\S]*?)['"]/i);
          if (match && match[1]) {
            clean = match[1].replace(/\\n/g, "\n");
          }
        }
        return clean;
      };

      const responseText = cleanResponseText(rawResponse);

      const aiMsg: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: responseText,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setMessages((prev) => [...prev, aiMsg]);
    } catch (err: any) {
      console.error("Error communicating with Site Visit AI agent:", err);
      const errorMsg: ChatMessage = {
        id: `err-${Date.now()}`,
        role: "assistant",
        content:
          err?.response?.data?.error ||
          "Sorry, I encountered an issue reaching the Site Visit AI server. Please verify your connection or backend agent service.",
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = () => {
    setMessages([]);
  };

  const handleClearActivityHistory = async () => {
    try {
      await apiService.delete("/api/venues/agent/activity/");
      setAgentEvents([]);
    } catch (err) {
      console.error("Failed to clear activity log:", err);
      setAgentEvents([]);
    }
  };

  const quickPrompts = [
    {
      shortLabel: "Site Visits",
      fullLabel: `What site visits are scheduled for ${venueData?.name || "this venue"
        }?`,
      icon: <EventNoteOutlined fontSize="small" />,
      query: `What site visits are scheduled for ${venueData?.name || "this venue"
        }?`,
    },
    {
      shortLabel: "Power & Wifi",
      fullLabel: `Check power backup and Wi-Fi setup`,
      icon: <ElectricBoltOutlined fontSize="small" />,
      query: `Check power backup and internet details for ${venueData?.name || "this venue"
        }.`,
    },
    {
      shortLabel: "Venue Health",
      fullLabel: `Analyze data completeness and venue health`,
      icon: <AnalyticsOutlined fontSize="small" />,
      query: `Analyze data completeness and readiness for ${venueData?.name || "this venue"
        }.`,
    },
  ];

  // Activity Event Filtering & Counts
  const whatsappCount = useMemo(
    () => agentEvents.filter((e) => e.event_type === "whatsapp").length,
    [agentEvents]
  );
  const sqlCount = useMemo(
    () => agentEvents.filter((e) => e.event_type === "sql").length,
    [agentEvents]
  );
  const searchCount = useMemo(
    () =>
      agentEvents.filter(
        (e) =>
          e.event_type === "search" ||
          e.event_type === "image_search" ||
          e.event_type === "web_scrape"
      ).length,
    [agentEvents]
  );
  const tasksCount = useMemo(
    () =>
      agentEvents.filter(
        (e) =>
          e.event_type === "task_start" ||
          e.event_type === "task_complete" ||
          e.event_type === "thought"
      ).length,
    [agentEvents]
  );

  const filteredEvents = useMemo(() => {
    return agentEvents.filter((event) => {
      if (activityFilter === "whatsapp" && event.event_type !== "whatsapp")
        return false;
      if (activityFilter === "sql" && event.event_type !== "sql") return false;
      if (
        activityFilter === "search" &&
        !["search", "image_search", "web_scrape"].includes(event.event_type)
      )
        return false;
      if (
        activityFilter === "tasks" &&
        !["task_start", "task_complete", "thought"].includes(event.event_type)
      )
        return false;

      if (activitySearchQuery.trim()) {
        const q = activitySearchQuery.toLowerCase();
        const titleMatch = event.title?.toLowerCase().includes(q);
        const detailsMatch = event.details?.toLowerCase().includes(q);
        return titleMatch || detailsMatch;
      }
      return true;
    });
  }, [agentEvents, activityFilter, activitySearchQuery]);

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: isOpen ? 24 : 100,
        right: 28,
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      {/* Floating Chat Modal / Drawer */}
      <Collapse in={isOpen} unmountOnExit sx={{ mb: 0 }}>
        <Paper
          elevation={12}
          sx={{
            width: { xs: "calc(100vw - 32px)", sm: 430 },
            maxHeight: 640,
            height: 570,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            border: `1px solid ${theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.12)"
              : "rgba(0, 0, 0, 0.08)"
              }`,
            backgroundColor:
              theme.palette.mode === "dark"
                ? colors.primary[400]
                : "#ffffff",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 20px 40px rgba(0,0,0,0.6)"
                : "0 20px 40px rgba(114, 78, 65, 0.2)",
          }}
        >
          {/* Main Top Header */}
          <Box
            sx={{
              p: 1.75,
              background: "linear-gradient(135deg, #724e41 0%, #8c5a45 100%)",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                variant="dot"
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: "#10b981",
                    color: "#10b981",
                    boxShadow: "0 0 0 2px white",
                    "&::after": {
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      animation: "ripple 1.2s infinite ease-in-out",
                      border: "1px solid currentColor",
                      content: '""',
                    },
                  },
                  "@keyframes ripple": {
                    "0%": { transform: "scale(.8)", opacity: 1 },
                    "100%": { transform: "scale(2.4)", opacity: 0 },
                  },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                    color: "#ffffff",
                    width: 38,
                    height: 38,
                  }}
                >
                  <BrandMIcon size={18} color="#ffffff" />
                </Avatar>
              </Badge>
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ lineHeight: 1.2, color: "#ffffff", fontSize: 15 }}
                >
                  Nyasha AI
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255, 255, 255, 0.8)", fontSize: 11 }}
                >
                  Site Visit AI Assistant
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {activeTab === "chat" ? (
                <Tooltip title="Clear chat history">
                  <IconButton
                    size="small"
                    onClick={handleClearHistory}
                    sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Clear activity log">
                  <IconButton
                    size="small"
                    onClick={handleClearActivityHistory}
                    sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                  >
                    <CleaningServicesOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Minimize modal">
                <IconButton
                  size="small"
                  onClick={() => setIsOpen(false)}
                  sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                >
                  <Minimize fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => setIsOpen(false)}
                sx={{ color: "rgba(255, 255, 255, 0.8)" }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Sleek Tab Switcher Bar */}
          <Box
            sx={{
              display: "flex",
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(0, 0, 0, 0.2)"
                  : "rgba(114, 78, 65, 0.08)",
              borderBottom: `1px solid ${theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(0, 0, 0, 0.06)"
                }`,
              px: 1,
            }}
          >
            <Box
              onClick={() => setActiveTab("chat")}
              sx={{
                py: 1,
                px: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                borderBottom:
                  activeTab === "chat" ? "2.5px solid #724e41" : "2.5px solid transparent",
                color:
                  activeTab === "chat"
                    ? "#724e41"
                    : theme.palette.mode === "dark"
                      ? "#94a3b8"
                      : "#64748b",
                fontWeight: activeTab === "chat" ? 700 : 500,
                fontSize: 13,
                transition: "all 0.2s ease",
                "&:hover": {
                  color: "#724e41",
                },
              }}
            >
              <ChatBubbleOutline sx={{ fontSize: 16 }} />
              <Typography variant="body2" fontWeight="inherit" fontSize="inherit">
                Interactive Chat
              </Typography>
            </Box>

            <Box
              onClick={() => setActiveTab("activity")}
              sx={{
                py: 1,
                px: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                borderBottom:
                  activeTab === "activity"
                    ? "2.5px solid #724e41"
                    : "2.5px solid transparent",
                color:
                  activeTab === "activity"
                    ? "#724e41"
                    : theme.palette.mode === "dark"
                      ? "#94a3b8"
                      : "#64748b",
                fontWeight: activeTab === "activity" ? 700 : 500,
                fontSize: 13,
                transition: "all 0.2s ease",
                position: "relative",
                "&:hover": {
                  color: "#724e41",
                },
              }}
            >
              <SensorsOutlined sx={{ fontSize: 16 }} />
              <Typography variant="body2" fontWeight="inherit" fontSize="inherit">
                Realtime Activity
              </Typography>

              {/* Pulsing Dot Indicator if active/thinking */}
              {(agentStatus?.status === "active" ||
                agentStatus?.status === "thinking") && (
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: "#10b981",
                      boxShadow: "0 0 6px #10b981",
                      animation: "pulseDot 1.2s infinite ease-in-out",
                      "@keyframes pulseDot": {
                        "0%": { transform: "scale(0.8)", opacity: 1 },
                        "50%": { transform: "scale(1.4)", opacity: 0.7 },
                        "100%": { transform: "scale(0.8)", opacity: 1 },
                      },
                    }}
                  />
                )}

              {agentEvents.length > 0 && (
                <Chip
                  label={agentEvents.length}
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: 10,
                    fontWeight: 700,
                    bgcolor:
                      activeTab === "activity"
                        ? "rgba(114, 78, 65, 0.15)"
                        : "rgba(0, 0, 0, 0.06)",
                    color: activeTab === "activity" ? "#724e41" : "inherit",
                    ml: 0.5,
                  }}
                />
              )}
            </Box>
          </Box>

          {/* TAB CONTENT: Interactive Chat */}
          {activeTab === "chat" && (
            <>
              {/* Subheader info if venue is loaded */}
              {venueData && (
                <Box
                  sx={{
                    px: 2,
                    py: 0.75,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(114, 78, 65, 0.06)",
                    borderBottom: `1px solid ${theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.06)"
                      }`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color:
                        theme.palette.mode === "dark" ? "#94a3b8" : "#5c3b30",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "260px",
                    }}
                  >
                    Context: {venueData.name} ({venueData.city || "Venue View"})
                  </Typography>
                  <Chip
                    label="AI Ready"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 10,
                      fontWeight: 700,
                      borderColor: "#724e41",
                      color: "#724e41",
                    }}
                    variant="outlined"
                  />
                </Box>
              )}

              {/* Quick suggestion chips */}
              {messages.length > 0 && (
                <Box
                  sx={{
                    p: 1.25,
                    display: "flex",
                    gap: 1,
                    overflowX: "auto",
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(0,0,0,0.15)"
                        : "rgba(241, 245, 249, 0.6)",
                    borderBottom: `1px solid ${theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)"
                      }`,
                    "::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {quickPrompts.map((item, idx) => (
                    <Chip
                      key={idx}
                      icon={item.icon}
                      label={item.shortLabel}
                      size="small"
                      clickable
                      onClick={() => handleSendMessage(item.query)}
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "#ffffff",
                        border: `1px solid ${theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(114, 78, 65, 0.15)"
                          }`,
                        "&:hover": {
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.16)"
                              : "rgba(114, 78, 65, 0.08)",
                        },
                      }}
                    />
                  ))}
                </Box>
              )}

              {/* Chat Body: Gemini Initial Screen vs Message List */}
              {messages.length === 0 ? (
                <Box
                  sx={{
                    flexGrow: 1,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    background:
                      theme.palette.mode === "dark"
                        ? colors.primary[500]
                        : "#fdfbf9",
                  }}
                >
                  <Box sx={{ mb: 3.5, width: "100%", textAlign: "left" }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: "-0.02em",
                        background:
                          theme.palette.mode === "dark"
                            ? "linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)"
                            : "linear-gradient(135deg, #724e41 0%, #2563eb 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        mb: 0.5,
                        fontSize: "1.75rem",
                      }}
                    >
                      Hie {userName || "there"}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color:
                          theme.palette.mode === "dark"
                            ? "#94a3b8"
                            : "#64748b",
                        fontSize: "1.05rem",
                      }}
                    >
                      whats the agenda today?
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    {quickPrompts.map((item, idx) => (
                      <Paper
                        key={idx}
                        elevation={0}
                        onClick={() => handleSendMessage(item.query)}
                        sx={{
                          p: 1.75,
                          borderRadius: 3,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.75,
                          transition:
                            "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.04)"
                              : "#ffffff",
                          border: `1px solid ${theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(0, 0, 0, 0.07)"
                            }`,
                          boxShadow:
                            theme.palette.mode === "dark"
                              ? "0 2px 8px rgba(0,0,0,0.2)"
                              : "0 2px 8px rgba(0,0,0,0.03)",
                          "&:hover": {
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.08)"
                                : "rgba(114, 78, 65, 0.04)",
                            borderColor: "#724e41",
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(114, 78, 65, 0.08)",
                            color: "#724e41",
                          }}
                        >
                          {item.icon}
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: 12.5,
                            color:
                              theme.palette.mode === "dark"
                                ? "#e2e8f0"
                                : "#334155",
                            flexGrow: 1,
                            lineHeight: 1.4,
                          }}
                        >
                          {item.fullLabel}
                        </Typography>
                        <East
                          fontSize="small"
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "#64748b"
                                : "#94a3b8",
                            fontSize: 16,
                          }}
                        />
                      </Paper>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    flexGrow: 1,
                    p: 2,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    background:
                      theme.palette.mode === "dark"
                        ? colors.primary[500]
                        : "#fdfbf9",
                  }}
                >
                  {messages.map((msg) => {
                    const isUser = msg.role === "user";
                    return (
                      <Box
                        key={msg.id}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: isUser ? "flex-end" : "flex-start",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            flexDirection: isUser ? "row-reverse" : "row",
                            maxWidth: "85%",
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              bgcolor: isUser
                                ? "#724e41"
                                : theme.palette.mode === "dark"
                                  ? "#5c3b30"
                                  : "#8c5a45",
                              color: "#ffffff",
                              fontSize: 12,
                            }}
                          >
                            {isUser ? (
                              <PersonOutline fontSize="small" />
                            ) : (
                              <BrandMIcon size={14} color="#ffffff" />
                            )}
                          </Avatar>

                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                              borderTopRightRadius: isUser ? 0 : 2.5,
                              borderTopLeftRadius: isUser ? 2.5 : 0,
                              backgroundColor: isUser
                                ? "#724e41"
                                : theme.palette.mode === "dark"
                                  ? colors.primary[400]
                                  : "#ffffff",
                              color: isUser
                                ? "#ffffff"
                                : theme.palette.mode === "dark"
                                  ? "#f1f5f9"
                                  : "#1e293b",
                              boxShadow: isUser
                                ? "0 2px 6px rgba(114, 78, 65, 0.25)"
                                : "0 2px 6px rgba(0, 0, 0, 0.05)",
                              border: isUser
                                ? "none"
                                : `1px solid ${theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.06)"
                                }`,
                            }}
                          >
                            <FormattedMessageText
                              content={msg.content}
                              isUser={isUser}
                            />
                          </Paper>
                        </Box>

                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: 10,
                            color: "text.disabled",
                            mt: 0.5,
                            px: 4,
                          }}
                        >
                          {msg.timestamp}
                        </Typography>
                      </Box>
                    );
                  })}

                  {loading && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1,
                        alignSelf: "flex-start",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          background:
                            "linear-gradient(135deg, #724e41 0%, #8c5a45 100%)",
                          color: "#ffffff",
                        }}
                      >
                        <BrandMIcon size={14} color="#ffffff" />
                      </Avatar>
                      <Paper
                        elevation={0}
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? colors.primary[400]
                              : "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <CircularProgress
                          size={14}
                          thickness={5}
                          sx={{ color: "#724e41" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ fontStyle: "italic", color: "text.secondary" }}
                        >
                          Nyasha AI thinking...
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                  <div ref={messagesEndRef} />
                </Box>
              )}

              <Divider />

              {/* Chat Input Field */}
              <Box
                sx={{
                  p: 1.5,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? colors.primary[400]
                      : "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Ask Nyasha about this venue..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  disabled={loading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      fontSize: 13,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? colors.primary[500]
                          : "#fcf8f5",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#724e41",
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleSendMessage()}
                          disabled={!input.trim() || loading}
                          sx={{
                            bgcolor: input.trim() ? "#724e41" : "transparent",
                            color: input.trim() ? "#ffffff" : "action.disabled",
                            "&:hover": {
                              bgcolor: "#5c3b30",
                            },
                            width: 32,
                            height: 32,
                          }}
                        >
                          <Send fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </>
          )}

          {/* TAB CONTENT: Real-Time Agent Activity Monitor */}
          {activeTab === "activity" && (
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? colors.primary[500]
                    : "#f8fafc",
              }}
            >
              {/* Agent Status & Metrics Grid Header */}
              <Box
                sx={{
                  p: 1.5,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.03)"
                      : "#ffffff",
                  borderBottom: `1px solid ${theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.06)"
                    }`,
                }}
              >
                {/* Live Status Bar */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    mb: 1.25,
                    borderRadius: 2,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(114, 78, 65, 0.2)"
                        : "rgba(114, 78, 65, 0.05)",
                    border: `1px solid ${theme.palette.mode === "dark"
                      ? "rgba(114, 78, 65, 0.4)"
                      : "rgba(114, 78, 65, 0.15)"
                      }`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor:
                          agentStatus?.status === "active" ||
                            agentStatus?.status === "thinking"
                            ? "#10b981"
                            : "#94a3b8",
                        boxShadow:
                          agentStatus?.status === "active" ||
                            agentStatus?.status === "thinking"
                            ? "0 0 8px #10b981"
                            : "none",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        color:
                          theme.palette.mode === "dark" ? "#f1f5f9" : "#334155",
                      }}
                    >
                      Status:{" "}
                      {agentStatus?.status === "active"
                        ? "Active Executing Task"
                        : agentStatus?.status === "thinking"
                          ? "Nyasha AI Thinking..."
                          : "Idle (Ready)"}
                    </Typography>
                  </Box>

                  <Chip
                    label="Live Stream"
                    size="small"
                    icon={<SensorsOutlined sx={{ fontSize: "12px !important" }} />}
                    sx={{
                      height: 20,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: "#10b981",
                      color: "#ffffff",
                      "& .MuiChip-icon": { color: "#ffffff" },
                    }}
                  />
                </Paper>

                {/* 4 Metric Counter Cards */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 1,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      textAlign: "center",
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "#f1f5f9",
                    }}
                  >
                    <StorageOutlined
                      sx={{ fontSize: 16, color: "#3b82f6", mb: 0.2 }}
                    />
                    <Typography variant="subtitle2" fontWeight={800} fontSize={13}>
                      {sqlCount}
                    </Typography>
                    <Typography variant="caption" fontSize={9} color="text.secondary">
                      SQL Queries
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      textAlign: "center",
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "#f1f5f9",
                    }}
                  >
                    <SearchOutlined
                      sx={{ fontSize: 16, color: "#8b5cf6", mb: 0.2 }}
                    />
                    <Typography variant="subtitle2" fontWeight={800} fontSize={13}>
                      {searchCount}
                    </Typography>
                    <Typography variant="caption" fontSize={9} color="text.secondary">
                      Searches
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      textAlign: "center",
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "#f1f5f9",
                    }}
                  >
                    <PhoneInTalkOutlined
                      sx={{ fontSize: 16, color: "#10b981", mb: 0.2 }}
                    />
                    <Typography variant="subtitle2" fontWeight={800} fontSize={13}>
                      {whatsappCount}
                    </Typography>
                    <Typography variant="caption" fontSize={9} color="text.secondary">
                      WhatsApp
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      textAlign: "center",
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "#f1f5f9",
                    }}
                  >
                    <SmartToyOutlined
                      sx={{ fontSize: 16, color: "#f59e0b", mb: 0.2 }}
                    />
                    <Typography variant="subtitle2" fontWeight={800} fontSize={13}>
                      {tasksCount}
                    </Typography>
                    <Typography variant="caption" fontSize={9} color="text.secondary">
                      Tasks
                    </Typography>
                  </Paper>
                </Box>
              </Box>

              {/* Filter Pills & Search Bar */}
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(0,0,0,0.2)"
                      : "#ffffff",
                  borderBottom: `1px solid ${theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.06)"
                    }`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    overflowX: "auto",
                    flexGrow: 1,
                    "::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  <Chip
                    label={`All (${agentEvents.length})`}
                    size="small"
                    onClick={() => setActivityFilter("all")}
                    sx={{
                      flexShrink: 0,
                      fontSize: 10,
                      fontWeight: 700,
                      height: 22,
                      bgcolor:
                        activityFilter === "all" ? "#724e41" : "transparent",
                      color: activityFilter === "all" ? "#ffffff" : "inherit",
                    }}
                  />
                  <Chip
                    label={`SQL (${sqlCount})`}
                    size="small"
                    onClick={() => setActivityFilter("sql")}
                    sx={{
                      flexShrink: 0,
                      fontSize: 10,
                      fontWeight: 700,
                      height: 22,
                      bgcolor:
                        activityFilter === "sql" ? "#724e41" : "transparent",
                      color: activityFilter === "sql" ? "#ffffff" : "inherit",
                    }}
                  />
                  <Chip
                    label={`Search (${searchCount})`}
                    size="small"
                    onClick={() => setActivityFilter("search")}
                    sx={{
                      flexShrink: 0,
                      fontSize: 10,
                      fontWeight: 700,
                      height: 22,
                      bgcolor:
                        activityFilter === "search" ? "#724e41" : "transparent",
                      color: activityFilter === "search" ? "#ffffff" : "inherit",
                    }}
                  />
                  <Chip
                    label={`WhatsApp (${whatsappCount})`}
                    size="small"
                    onClick={() => setActivityFilter("whatsapp")}
                    sx={{
                      flexShrink: 0,
                      fontSize: 10,
                      fontWeight: 700,
                      height: 22,
                      bgcolor:
                        activityFilter === "whatsapp"
                          ? "#724e41"
                          : "transparent",
                      color:
                        activityFilter === "whatsapp" ? "#ffffff" : "inherit",
                    }}
                  />
                  <Chip
                    label={`Tasks (${tasksCount})`}
                    size="small"
                    onClick={() => setActivityFilter("tasks")}
                    sx={{
                      flexShrink: 0,
                      fontSize: 10,
                      fontWeight: 700,
                      height: 22,
                      bgcolor:
                        activityFilter === "tasks" ? "#724e41" : "transparent",
                      color: activityFilter === "tasks" ? "#ffffff" : "inherit",
                    }}
                  />
                </Box>

                <TextField
                  size="small"
                  placeholder="Filter logs..."
                  value={activitySearchQuery}
                  onChange={(e) => setActivitySearchQuery(e.target.value)}
                  sx={{
                    width: 110,
                    "& .MuiOutlinedInput-root": {
                      height: 24,
                      fontSize: 11,
                      borderRadius: 1.5,
                    },
                  }}
                />
              </Box>

              {/* Event Stream List Feed */}
              <Box
                sx={{
                  flexGrow: 1,
                  p: 1.5,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  "::-webkit-scrollbar": {
                    width: 6,
                  },
                  "::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "::-webkit-scrollbar-thumb": {
                    background:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.2)"
                        : "rgba(114, 78, 65, 0.2)",
                    borderRadius: 3,
                  },
                  "::-webkit-scrollbar-thumb:hover": {
                    background:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.4)"
                        : "rgba(114, 78, 65, 0.4)",
                  },
                }}
              >
                {filteredEvents.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      px: 2,
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "text.secondary",
                    }}
                  >
                    <SmartToyOutlined
                      sx={{ fontSize: 44, opacity: 0.4, mb: 1, color: "#724e41" }}
                    />
                    <Typography variant="subtitle2" fontWeight={700}>
                      No real-time activities logged yet
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 0.5, fontSize: 11 }}>
                      Events will stream live when Nyasha processes site visits,
                      SQL queries, or chat prompts.
                    </Typography>
                    <Chip
                      label="Ask Nyasha a Query to See Live Actions"
                      clickable
                      onClick={() => {
                        setActiveTab("chat");
                        handleSendMessage("Check power backup and Wi-Fi details");
                      }}
                      size="small"
                      sx={{
                        mt: 1.5,
                        bgcolor: "#724e41",
                        color: "#ffffff",
                        fontWeight: 700,
                        fontSize: 11,
                        "&:hover": { bgcolor: "#5c3b30" },
                      }}
                    />
                  </Box>
                ) : (
                  filteredEvents.map((event) => {
                    const isExpanded = expandedEventId === event.id;

                    let Icon = SmartToyOutlined;
                    let iconBg = "rgba(114, 78, 65, 0.1)";
                    let iconColor = "#724e41";

                    if (event.event_type === "whatsapp") {
                      Icon = PhoneInTalkOutlined;
                      iconBg = "rgba(16, 185, 129, 0.15)";
                      iconColor = "#10b981";
                    } else if (event.event_type === "sql") {
                      Icon = StorageOutlined;
                      iconBg = "rgba(59, 130, 246, 0.15)";
                      iconColor = "#3b82f6";
                    } else if (
                      event.event_type === "search" ||
                      event.event_type === "image_search" ||
                      event.event_type === "web_scrape"
                    ) {
                      Icon = SearchOutlined;
                      iconBg = "rgba(139, 92, 246, 0.15)";
                      iconColor = "#8b5cf6";
                    } else if (event.event_type === "task_start") {
                      Icon = AutoAwesome;
                      iconBg = "rgba(245, 158, 11, 0.15)";
                      iconColor = "#f59e0b";
                    } else if (event.event_type === "task_complete") {
                      Icon = CheckCircleOutline;
                      iconBg = "rgba(16, 185, 129, 0.15)";
                      iconColor = "#10b981";
                    } else if (event.event_type === "error") {
                      Icon = ErrorOutline;
                      iconBg = "rgba(239, 68, 68, 0.15)";
                      iconColor = "#ef4444";
                    }

                    return (
                      <Paper
                        key={event.id}
                        elevation={0}
                        sx={{
                          flexShrink: 0,
                          minHeight: 48,
                          borderRadius: 2,
                          overflow: "hidden",
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? colors.primary[400]
                              : "#ffffff",
                          border: `1px solid ${theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(0, 0, 0, 0.06)"
                            }`,
                          transition: "all 0.2s ease",
                        }}
                      >
                        {/* Event Card Header */}
                        <Box
                          onClick={() =>
                            setExpandedEventId(isExpanded ? null : event.id)
                          }
                          sx={{
                            p: 1.25,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                            "&:hover": {
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.04)"
                                  : "rgba(0, 0, 0, 0.02)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.2,
                              overflow: "hidden",
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                bgcolor: iconBg,
                                color: iconColor,
                              }}
                            >
                              <Icon fontSize="small" />
                            </Avatar>

                            <Box sx={{ overflow: "hidden" }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                sx={{
                                  fontSize: 12,
                                  lineHeight: 1.2,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: 200,
                                }}
                              >
                                {event.title}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontSize={10}
                              >
                                {event.formatted_time || event.timestamp}
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Chip
                              label={event.status}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 9,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                bgcolor:
                                  event.status === "success"
                                    ? "rgba(16, 185, 129, 0.15)"
                                    : event.status === "running"
                                      ? "rgba(245, 158, 11, 0.15)"
                                      : event.status === "error"
                                        ? "rgba(239, 68, 68, 0.15)"
                                        : "rgba(148, 163, 184, 0.15)",
                                color:
                                  event.status === "success"
                                    ? "#10b981"
                                    : event.status === "running"
                                      ? "#f59e0b"
                                      : event.status === "error"
                                        ? "#ef4444"
                                        : "#64748b",
                              }}
                            />
                            {isExpanded ? (
                              <ExpandLess fontSize="small" color="action" />
                            ) : (
                              <ExpandMore fontSize="small" color="action" />
                            )}
                          </Box>
                        </Box>

                        {/* Event Card Body Details */}
                        {isExpanded && event.details && (
                          <Box
                            sx={{
                              px: 1.5,
                              pb: 1.5,
                              pt: 0.5,
                              borderTop: `1px solid ${theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.05)"
                                : "rgba(0, 0, 0, 0.04)"
                                }`,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(0, 0, 0, 0.15)"
                                  : "rgba(248, 250, 252, 0.8)",
                            }}
                          >
                            {event.event_type === "sql" ? (
                              <Box
                                component="pre"
                                sx={{
                                  m: 0,
                                  p: 1.25,
                                  borderRadius: 1.5,
                                  fontSize: 11,
                                  fontFamily: "monospace",
                                  bgcolor:
                                    theme.palette.mode === "dark"
                                      ? "#0f172a"
                                      : "#1e293b",
                                  color: "#38bdf8",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  overflowX: "auto",
                                }}
                              >
                                <code>{event.details}</code>
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: 11.5,
                                  color: "text.primary",
                                  whiteSpace: "pre-wrap",
                                  lineHeight: 1.5,
                                }}
                              >
                                {event.details}
                              </Typography>
                            )}

                            {event.extra &&
                              Object.keys(event.extra).length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color: "text.secondary",
                                    }}
                                  >
                                    Payload Extra:
                                  </Typography>
                                  <Box
                                    component="pre"
                                    sx={{
                                      m: 0,
                                      mt: 0.3,
                                      p: 1,
                                      borderRadius: 1,
                                      fontSize: 10.5,
                                      fontFamily: "monospace",
                                      bgcolor:
                                        theme.palette.mode === "dark"
                                          ? "rgba(0,0,0,0.3)"
                                          : "rgba(0,0,0,0.04)",
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    <code>
                                      {JSON.stringify(event.extra, null, 2)}
                                    </code>
                                  </Box>
                                </Box>
                              )}
                          </Box>
                        )}
                      </Paper>
                    );
                  })
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Collapse>

      {/* Floating Action Button (FAB) - hidden when chat is open */}
      {!isOpen && (
        <Tooltip title="Open Site Visit AI Chat" placement="left">
          <Fab
            variant="extended"
            onClick={() => setIsOpen(true)}
            sx={{
              px: 2.5,
              height: 48,
              borderRadius: 6,
              background: "linear-gradient(135deg, #724e41 0%, #8c5a45 100%)",
              color: "#ffffff",
              boxShadow: "0 8px 24px rgba(114, 78, 65, 0.45)",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: 0.3,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: "linear-gradient(135deg, #5c3b30 0%, #724e41 100%)",
                transform: "scale(1.05)",
                boxShadow: "0 12px 30px rgba(114, 78, 65, 0.6)",
              },
            }}
          >
            <AutoAwesome
              sx={{
                mr: 1.2,
                fontSize: 20,
                color: "#ffffff",
                animation: "pulse 2.2s infinite",
                "@keyframes pulse": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.15)" },
                  "100%": { transform: "scale(1)" },
                },
              }}
            />
            Site Visit AI
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
};

export default SiteVisitAiFloatingChat;
